/**
 * Engine API Service
 *
 * API client for workflow engine distributed orchestration endpoints.
 * Uses the workflowApi client (port 8001).
 */

import { workflowApi } from './workflowApi';

// Types

export interface EngineInstance {
  _id: string;
  instanceId: string;
  name?: string;
  clusterId: string;
  labels: Record<string, string>;
  resources: string[];
  environments?: string[];
  status: 'online' | 'offline' | 'draining';
  lastHeartbeat: string;
  startedAt: string;
  version: string;
  metadata: {
    hostname: string;
    ip?: string;
    port?: number;
    platform: string;
  };
}

export interface ExecutionClaim {
  _id: string;
  claimId: string;
  workflowId: string;
  triggerId: string;
  triggerType: string;
  triggerData: any;
  status: 'pending' | 'claimed' | 'executing' | 'completed' | 'failed';
  claimedBy?: string;
  claimedAt?: string;
  completedAt?: string;
  executionId?: string;
  failReason?: string;
  createdAt: string;
}

export interface WorkflowAffinity {
  preferredInstance?: string;
  requiredInstance?: string;
  preferredCluster?: string;
  requiredCluster?: string;
  requiredLabels?: Record<string, string>;
  requiredResources?: string[];
}

export interface EngineMetrics {
  period: string;
  totalClaims: number;
  completed: number;
  failed: number;
  active: number;
  byTriggerType: Record<string, number>;
  instanceDistribution: Array<{ instanceId: string; claims: number }>;
}

export interface EngineStats {
  executionsPerMinute: Array<{ minute: string; count: number }>;
  avgDurationMs: number;
  minDurationMs: number;
  maxDurationMs: number;
  successRate: number;
  totalLastHour: number;
  completedLastHour: number;
  failedLastHour: number;
  activeNow: number;
  errorsByCode: Array<{ code: string; count: number }>;
  slowestWorkflows: Array<{
    workflowId: string;
    workflowName: string;
    avgDurationMs: number;
    maxDurationMs: number;
    executions: number;
  }>;
  recentErrors: Array<{
    workflowId: string;
    workflowName: string;
    errorCode: string;
    errorMessage: string;
    nodeId: string | null;
    startedAt: string;
  }>;
}

export interface WorkflowWithAffinity {
  _id: string;
  name: string;
  status: string;
  affinity: WorkflowAffinity;
  runEnvironments?: string[];
  owner_type: string;
  owner_id: string;
  ownerName?: string;
}

// API Methods

export const engineApi = {
  getInstances: async (): Promise<{ instances: EngineInstance[]; currentInstance: string }> => {
    const res = await workflowApi.get<{ success: boolean; data: { instances: EngineInstance[]; currentInstance: string } }>('/api/engine/instances');
    return res.data;
  },

  getCurrentInstance: async () => {
    const res = await workflowApi.get<{ success: boolean; data: any }>('/api/engine/instances/current');
    return res.data;
  },

  getClaims: async (filters?: { workflowId?: string; status?: string; instanceId?: string; limit?: number }): Promise<{ claims: ExecutionClaim[] }> => {
    const params = new URLSearchParams();
    if (filters?.workflowId) params.set('workflowId', filters.workflowId);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.instanceId) params.set('instanceId', filters.instanceId);
    if (filters?.limit) params.set('limit', String(filters.limit));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await workflowApi.get<{ success: boolean; data: { claims: ExecutionClaim[] } }>(`/api/engine/claims${query}`);
    return res.data;
  },

  getMetrics: async (): Promise<EngineMetrics> => {
    const res = await workflowApi.get<{ success: boolean; data: EngineMetrics }>('/api/engine/metrics');
    return res.data;
  },

  getStats: async (): Promise<EngineStats> => {
    const res = await workflowApi.get<{ success: boolean; data: EngineStats }>('/api/engine/stats');
    return res.data;
  },

  getWorkflowsWithAffinity: async (): Promise<{ workflows: WorkflowWithAffinity[] }> => {
    const res = await workflowApi.get<{ success: boolean; data: { workflows: WorkflowWithAffinity[] } }>('/api/engine/workflows-with-affinity');
    return res.data;
  },

  updateWorkflowAffinity: async (workflowId: string, affinity: WorkflowAffinity) => {
    const res = await workflowApi.patch<{ success: boolean; data: any }>(`/api/engine/workflows/${workflowId}/affinity`, affinity);
    return res.data;
  },

  clearWorkflowAffinity: async (workflowId: string) => {
    const res = await workflowApi.delete<{ success: boolean; data: any }>(`/api/engine/workflows/${workflowId}/affinity`);
    return res.data;
  },

  updateWorkflowEnvironments: async (workflowId: string, runEnvironments: string[]) => {
    const res = await workflowApi.patch<{ success: boolean; data: any }>(`/api/engine/workflows/${workflowId}/environments`, { runEnvironments });
    return res.data;
  },

  toggleWorkflowStatus: async (workflowId: string) => {
    const res = await workflowApi.post<{ success: boolean; data: any }>(`/api/engine/workflows/${workflowId}/toggle-status`, {});
    return res.data;
  },
};
